import React, { Component } from "react";
import ImageMap from "./ImageMap";
import MapActionBar from "./mapUtils/MapActionBar";

import ImageLayer from "ol/layer/Image";
import ImageCanvasSource from "ol/source/ImageCanvas";
import { toContext } from "ol/render";

import {
  drawPlayer,
  drawPlayerPosition,
  drawPlayerSpeed,
  drawPlayerJersey,
  setPointStyling,
  drawCanvasBall,
  validateSpecialCtxStyling,
  drawTrailLines,
  setMultiLineStyling,
  drawPlayerSelection,
} from "./mapStyles/mapStyles";

import { BALL, TRAILS_STORAGE_LIMIT } from "../../../../config/constants";
import {
  checkIfPlayerIsSelected,
  removeDecimalsFromSecs,
  shouldTrailPlayer,
  removeTrailsIfExceed,
  fpsToMphConverter,
  isPVBall,
} from "../../../../helpers/helpers";
import {
  PracticeMapProps,
  PracticeMapState,
  PlayersTrails,
  Blinks,
  Blink,
  Tracks,
  MapCanvas,
} from "../types";
import { Extent } from "ol/extent";
import CanvasImmediateRenderer from "ol/render/canvas/Immediate";
import { Map, MapBrowserEvent } from "ol";
import { Coordinate } from "ol/coordinate";
import { PinnedRows } from "../../../../store/types";

const mapCanvas: MapCanvas = {
  canvas: undefined,
  ctx: undefined,
  toCtx: undefined,
  canvasWidth: undefined,
  canvasHeight: undefined,
};

/**
 * Method to get the pixel coords of the map based on the zoom/resolution behaviour
 * @param {ol} practiceMap current openlayers map, declarated on ImageLayer
 * @param {Array[]} canvasExtent extent of the canvas, declared on the canvasFunction
 * @return the x and y delta positions.
 */
const getMapDeltaCoordinates = (practiceMap: Map, canvasExtent: Extent) => {
  const mapExtent = practiceMap
    .getView()
    .calculateExtent(practiceMap.getSize());
  const canvasOrigin = practiceMap.getPixelFromCoordinate([
    canvasExtent[0],
    canvasExtent[3],
  ]);
  const mapOrigin = practiceMap.getPixelFromCoordinate([
    mapExtent[0],
    mapExtent[3],
  ]);
  return [mapOrigin[0] - canvasOrigin[0], mapOrigin[1] - canvasOrigin[1]];
};

/**
 * Gets the last Map Layer, in this case, the canvas declared one.
 * @param {ol} practiceMap current openlayers map, declarated on ImageLayer
 */
const getImageLayer = (practiceMap: Map) =>
  practiceMap.getLayers().getArray().slice(-1);

// MUST ADD BLINKS PROPERTIES
const sortBlinksIfFlagged = (blinks: Blinks, pinnedRows: PinnedRows) => {
  if (pinnedRows && pinnedRows.length) {
    const unFlaggedBlinks: Blinks = [],
      flaggedBlinks: Blinks = [],
      balls: Blinks = [];
    blinks.forEach((blink: Blink) => {
      const isPlayerSelected = pinnedRows.includes(blink.player.playerId);
      if (blink.player.name !== BALL) {
        if (isPlayerSelected) flaggedBlinks.push(blink);
        else unFlaggedBlinks.push(blink);
      } else {
        balls.push(blink);
      }
    });
    return [...unFlaggedBlinks, ...flaggedBlinks, ...balls];
  }

  return blinks;
};

export default class PracticeMap extends Component<
  PracticeMapProps,
  PracticeMapState
> {
  constructor(props: PracticeMapProps) {
    super(props);
    this.canvasExtent = undefined;
    this.lastTrackSec = 0;
    this.lastTrack = [];
    this.tracksTimeOut = undefined;
  }

  /* Global Types */
  canvasExtent: Extent | undefined;
  lastTrackSec: number;
  lastTrack: Blinks;
  tracksTimeOut: ReturnType<typeof setTimeout> | undefined;

  state: PracticeMapState = {
    practiceMap: undefined,
    blinkFlag: new Date(),
  };

  componentDidUpdate(prevProps: PracticeMapProps, prevState: PracticeMapState) {
    // On the map creation, add the new ImageCanvas Layer and add a click listener for players
    if (prevState.practiceMap !== this.state.practiceMap) {
      this.addMapCanvas();
      //REVIEW TYPE
      this.state.practiceMap!.on("click", (event: MapBrowserEvent<any>) => {
        this.handleOnSelectPlayers(event.coordinate);
      });
    }
  }

  shouldComponentUpdate(
    nextProps: PracticeMapProps,
    nextState: PracticeMapState
  ) {
    // re-render *only map* on props or state change
    if (
      this.props.show !== nextProps.show ||
      this.props.field !== nextProps.field ||
      this.props.blinks !== nextProps.blinks ||
      this.props.currentSec !== nextProps.currentSec ||
      this.props.pinnedRows !== nextProps.pinnedRows ||
      this.state.practiceMap !== nextState.practiceMap ||
      this.state.blinkFlag !== nextState.blinkFlag
    ) {
      const { hasBlinks } = nextProps;
      const { practiceMap } = nextState;
      if (hasBlinks && practiceMap) {
        const [canvasLayer] = getImageLayer(practiceMap) as any;
        canvasLayer.getSource().changed();
      }
    }

    // on this props changes, rerender the component
    if (
      this.props.show !== nextProps.show ||
      this.props.field !== nextProps.field ||
      this.state.practiceMap !== nextState.practiceMap
    ) {
      return true;
    }

    return false;
  }

  /* Create the CanvasLayer and add it to the map, also the canvasFunction will rerender
  each time the map resolution/zoom changes or when forced, like on the first conditional on
  the shouldComponentUpdate. */
  addMapCanvas = () => {
    const { practiceMap } = this.state;
    const canvasLayer = new ImageLayer({
      source: new ImageCanvasSource({
        canvasFunction: (extent, resolution, pixelRatio, size, projection) =>
          this.canvasFunction(extent, pixelRatio, size as [number, number]),
        projection: practiceMap!.getView().getProjection(),
      }),
    });
    canvasLayer.setZIndex(10);
    practiceMap!.addLayer(canvasLayer);
  };

  /**
   * Method that creates the context and allows to draw on the canvas.
   * @param {Array} canvasExtent canvasLayer extent on the map
   * @param {number} canvasPixelRatio pixel variation on the different zoom/resolution levels
   * @param {Array} size [width, height] size of the current layer on the map
   * @returns modified canvas, so the layerknows the changes.
   */
  canvasFunction = (
    canvasExtent: Extent,
    canvasPixelRatio: number,
    size: [number, number]
  ) => {
    const [width, height] = size;
    const { canvasWidth, canvasHeight }: MapCanvas = mapCanvas;

    if (!mapCanvas.canvas || canvasWidth !== width || canvasHeight !== height) {
      const canvas = document.createElement("canvas");
      canvas.setAttribute("width", width.toString());
      canvas.setAttribute("height", height.toString());

      const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
      const toCtx = toContext(ctx, {
        size: [width, height],
        pixelRatio: canvasPixelRatio,
      });

      const obj = {
        canvas,
        ctx,
        toCtx,
        canvasWidth: width,
        canvasHeight: height,
      };
      Object.entries(obj).forEach(
        ([key, value]) => ((mapCanvas as any)[key] = value)
      ); // update mapCanvas properties
    }

    mapCanvas.ctx!.clearRect(
      0,
      0,
      mapCanvas.canvasWidth!,
      mapCanvas.canvasHeight!
    ); // clear all draws on the canvas
    this.drawCanvasFeatures(canvasExtent, canvasPixelRatio);
    return mapCanvas.canvas!;
  };

  /**
   * Method to check if there is a gap between the data and should greyish all the players.
   * @param {Array} blinks current tracks blink (a full second tracks data)
   * @param {number} currentSec current viewer second in decimals.
   * @param {Array} pinnedRows flagged players
   */
  repeatBlinksIfGapped = (
    blinks: Tracks,
    currentSec: number,
    pinnedRows: PinnedRows
  ) => {
    const blinkPos = currentSec % 10;
    const actualSec =
      currentSec > 0 ? removeDecimalsFromSecs(currentSec) : currentSec;
    this.tracksTimeOut && clearTimeout(this.tracksTimeOut);
    if (blinks && blinks[actualSec] && blinks[actualSec][blinkPos]) {
      this.lastTrackSec = currentSec;
      this.lastTrack = blinks[actualSec][blinkPos];
      return sortBlinksIfFlagged(blinks[actualSec][blinkPos], pinnedRows);
    } else {
      if (currentSec - this.lastTrackSec >= 300) {
        this.lastTrack = [];
        this.setState({ blinkFlag: new Date() });
      }
      return this.lastTrack.length ? this.lastTrack : [];
    }
  };
  /* Main method to get all tracks positions and adapt it to the current map view,
  the updated tracks positions record for trails if flagged and are sent to draw in canvas */
  drawCanvasFeatures = (canvasExtent: Extent, canvasPixelRatio: number) => {
    const { practiceMap } = this.state;
    const { blinks, field, currentSec, pinnedRows } = this.props;
    const [xDelta, yDelta] = getMapDeltaCoordinates(practiceMap!, canvasExtent);
    if (
      ((blinks && Object.keys(blinks).length) || this.lastTrack.length) &&
      !field.isLoading
    ) {
      this.repeatBlinksIfGapped(blinks, currentSec, pinnedRows).forEach(
        (blink) => {
          const [blinkPosX, blinkPosY] = this.blinksWithinBoundingBox(
            blink.posX,
            blink.posY
          );
          const [xPixelPos, yPixelPos] =
            practiceMap!.getPixelFromCoordinate([blinkPosX, blinkPosY]) || [];
          const [xPlayerPos, yPlayerPos] = [
            xPixelPos + xDelta,
            yPixelPos + yDelta,
          ];
          this.canvasExtent = canvasExtent;
          this.recordPlayerTrails(
            blinkPosX,
            blinkPosY,
            blink.player.playerId,
            currentSec
          );
          this.drawPlayerInCanvas(
            xPlayerPos,
            yPlayerPos,
            blink,
            canvasPixelRatio
          );
        }
      );
    }
  };

  /**
   * Method to record and clear if exceed the current flagged trails.
   * @param {number} xPlayerPos x player position.
   * @param {number} yPlayerPos y player position.
   * @param {number} playerId id of the current player.
   * @param {number} index current second in decimals of the viewer.
   */
  recordPlayerTrails = (
    xPlayerPos: number,
    yPlayerPos: number,
    playerId: string,
    index: number
  ) => {
    const { pinnedRows, currentSec, setPlayersTrails } = this.props;
    if (checkIfPlayerIsSelected(pinnedRows, playerId)) {
      const unmutedTrails = { ...this.props.playersTrails };
      const playerTrails = unmutedTrails[playerId]
        ? unmutedTrails[playerId]
        : {};
      if (!playerTrails[index] && this.lastTrackSec === currentSec) {
        // compare if it's not a gap
        const lastTrailKey =
          Object.keys(playerTrails)[Object.keys(playerTrails).length - 1];
        const [, endTrails] = playerTrails[lastTrailKey]
          ? playerTrails[lastTrailKey]
          : [[], [xPlayerPos, yPlayerPos]];
        playerTrails[index] = [endTrails, [xPlayerPos, yPlayerPos]];
        unmutedTrails[playerId] = removeTrailsIfExceed(playerTrails);
        setPlayersTrails(unmutedTrails);
      }
    }
  };

  /**
   * Method that fires all type of drawing on the canvas, based on its selected filters.
   * @param {number} x x position of the track.
   * @param {number} y y position of the track
   * @param {Array} blinks current track blink (in decimal of second)
   * @param {number} canvasPixelRatio the canvasPixelRatio (only for the pure canvas drawings).
   */
  drawPlayerInCanvas = (
    x: number,
    y: number,
    blink: Blink,
    canvasPixelRatio: number
  ) => {
    const { toCtx, ctx } = mapCanvas;
    const { speed, player } = blink;
    const { show: filters } = this.props;

    if (!isPVBall(player)) {
      const { currentSec, pinnedRows, playersTrails } = this.props;
      const playerColor = validateSpecialCtxStyling(
        ctx!,
        player,
        currentSec,
        pinnedRows,
        this.lastTrackSec
      );

      if (
        filters.trails &&
        checkIfPlayerIsSelected(pinnedRows, player.playerId)
      ) {
        this.getTrailsPixelCoords(
          playersTrails,
          player.playerId,
          toCtx!,
          playerColor
        );
      }

      if (filters.positions) {
        drawPlayerPosition(
          ctx!,
          toCtx!,
          x,
          y,
          player.position,
          canvasPixelRatio
        );
      }

      setPointStyling(toCtx!, x, y, drawPlayer(playerColor));

      if (filters.jerseys) {
        setPointStyling(toCtx!, x, y, drawPlayerJersey(player.jersey));
      }

      if (filters.speed) {
        drawPlayerSpeed(
          ctx!,
          toCtx!,
          x,
          y,
          fpsToMphConverter(speed),
          canvasPixelRatio
        );
      }
    } else if (filters.balls) {
      drawCanvasBall(ctx!, x, y, canvasPixelRatio);
    }
  };

  /**
   * Method that draws the player trails on the canvas.
   * @param {Object} playerTrails all of the current saved trails.
   * @param {number} playerId id of the current selected player.
   * @param {Object} toCtx canvas propertie to draw openlayers-canvas elements.
   * @param {string} playerColor color of the current player.
   */
  getTrailsPixelCoords = (
    playerTrails: PlayersTrails,
    playerId: string,
    toCtx: CanvasImmediateRenderer,
    playerColor: string
  ) => {
    const [xDelta, yDelta] = getMapDeltaCoordinates(
      this.state.practiceMap!,
      this.canvasExtent!
    );
    const trailsObject = shouldTrailPlayer(playerTrails, playerId);
    const { currentSec } = this.props;
    const trailPos: Array<Array<Coordinate>> = [];

    trailsObject.forEach(([key, trail]: any) => {
      if (key <= currentSec && currentSec - key <= TRAILS_STORAGE_LIMIT) {
        const [startPos, endPos] = trail;
        const [xPos, yPos] =
          this.state.practiceMap!.getPixelFromCoordinate(startPos) || [];
        const [xEndPos, yEndPos] =
          this.state.practiceMap!.getPixelFromCoordinate(endPos) || [];
        trailPos.push([
          [xPos + xDelta, yPos + yDelta],
          [xEndPos + xDelta, yEndPos + yDelta],
        ]);
      }
    });

    if (trailPos.length) {
      setMultiLineStyling(toCtx, trailPos, drawTrailLines(playerColor));
    }
  };

  /* Transform position pixels into the team field dimension in feets. */
  blinksWithinBoundingBox = (blinkPosX: number, blinkPosY: number) => {
    const { x0, y0, x1, y1, widthFt, heightFt } = this.props.field;
    const [xPixelToFeet, yPixelToFeet] = [
      (x1 - x0) / widthFt,
      (y1 - y0) / heightFt,
    ];
    return [blinkPosX * xPixelToFeet + x0, blinkPosY * yPixelToFeet + y0];
  };

  /**
   * Method to check if a player is clicked on the map.
   * @param {number} coordinate click event coordinate
   */
  handleOnSelectPlayers = (coordinates: Coordinate) => {
    const { ctx } = mapCanvas;
    const { practiceMap } = this.state;
    const { blinks, currentSec, pinnedRows, changeTablePropertiesPinnedRows } =
      this.props;

    const unmutedRows = [...pinnedRows];

    if (blinks && Object.keys(blinks).length) {
      const actualSec =
        currentSec > 0 ? removeDecimalsFromSecs(currentSec) : currentSec;
      const blinkPos = currentSec % 10;

      if (blinks[actualSec]) {
        const actualBlinks = [...blinks[actualSec][blinkPos]];
        const resolution = practiceMap!.getView().getResolution();

        actualBlinks.reverse().some((blink) => {
          const [blinkX, blinkY] = this.blinksWithinBoundingBox(
            blink.posX,
            blink.posY
          );
          drawPlayerSelection(ctx!, blinkX, blinkY, resolution as number);

          if (
            blink.player.playerId &&
            !isPVBall(blink.player) &&
            ctx!.isPointInPath(coordinates[0], coordinates[1])
          ) {
            const playerIndex = unmutedRows.indexOf(blink.player.playerId);
            if (playerIndex > -1) unmutedRows.splice(playerIndex, 1);
            else unmutedRows.push(blink.player.playerId);
            changeTablePropertiesPinnedRows(unmutedRows);
            return true;
          }
          return false;
        });
      }
    }
  };

  render() {
    const { practiceMap } = this.state;
    const { show, field, mapImage, setField, setOnShow, dimensions } =
      this.props;

    return (
      <div className="map-body">
        <ImageMap
          field={field}
          setField={setField}
          mapImage={mapImage}
          dimensions={dimensions}
          practiceMap={practiceMap}
          setPracticeMap={(map: Map) =>
            this.setState({ ...this.state, practiceMap: map })
          }
        />

        <div className="map-action-bar">
          <MapActionBar show={show} setOnShow={setOnShow} />
        </div>
      </div>
    );
  }
}
